import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import ParallaxImage from "@/components/ui/ParallaxImage";
import LuxuryCard from "@/components/ui/LuxuryCard";
import BookingWidget from "@/components/ui/BookingWidget";
import { apiFetch } from "@/utils/api";
import { useSEO } from "@/utils/useSEO";
import { useTranslation } from "react-i18next";
import { mockRooms } from "@/utils/roomsData";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function AccommodationDetail() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [room, setRoom] = useState<any>(() => mockRooms.find((v) => v.slug === slug) || mockRooms[0]);
  const [allRooms, setAllRooms] = useState<any[]>(mockRooms);
  const [loading, setLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const roomDesc = room
    ? `${room.title} at Patal Kikian Villas Sidemen. ${room.description} Enjoy breathtaking views of Mount Agung and Bali's lush rice terraces from your private luxury villa.`
    : undefined;

  useSEO(
    room?.title || "Luxury Villa Room",
    roomDesc,
    room?.image,
    { type: "room", price: room?.price, roomSize: room?.roomSize }
  );

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null || !room?.gallery) return;
      if (e.key === "Escape") {
        setLightboxIndex(null);
      } else if (e.key === "ArrowRight") {
        setLightboxIndex((prev) => (prev !== null && prev < room.gallery.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowLeft") {
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : room.gallery.length - 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, room?.gallery]);

  useEffect(() => {
    const found = mockRooms.find((v) => v.slug === slug);
    setRoom(found || mockRooms[0]);
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading || !room) {
    return (
      <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background text-white">
        <p className="text-xl uppercase tracking-widest animate-pulse">{t("accommodation.detail.loading")}</p>
      </div>
    );
  }

  const title = t(`rooms.${slug}.title`, { defaultValue: room.title });
  const description = t(`rooms.${slug}.description`, { defaultValue: room.description }) || "Experience the ultimate luxury tropical minimalist room in Bali. A harmonious blend of contemporary luxury and traditional aesthetics.";
  const image = room.image || "/media/rooms/deluxe-bungalow/deluxe-bungalow (5).webp";
  const features = room.features?.length > 0 ? room.features : ["1 Bedroom", "Private Pool", "Garden View"];
  const amenities = room.amenities?.length > 0 && room.amenities[0] !== "" ? room.amenities : ["King-sized beds", "En-suite bathrooms", "Infinity pool", "High-speed Wi-Fi"];
  const inclusions = room.inclusions || [];
  const noted = room.noted || [];
  const roomSize = room.roomSize || "80 sqm";
  const bedSize = room.bedSize || "1 King Bed";
  const view = t(`rooms.${slug}.view`, { defaultValue: room.view }) || "Tropical Garden View";
  const roomRules = t(`rooms.${slug}.roomRules`, { defaultValue: room.roomRules }) || "No smoking inside. Quiet hours after 10 PM. Check-in at 2 PM, Check-out at 12 PM.";

  // Filter other rooms for recommendation and slice to ensure it fits grid
  const otherRooms = allRooms.filter((r) => r.slug !== slug).slice(0, 2);

  return (
    <>
      <section className="relative min-h-[100dvh] w-full flex flex-col items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <ParallaxImage
            src={image}
            alt={title}
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-black/40 z-10" />
        </div>
        
        <div className="relative z-20 container mx-auto px-6 flex flex-col justify-center flex-grow pt-24 pb-12">
          <span className="text-gold uppercase tracking-widest text-sm mb-4 font-semibold">
            {t("accommodation.detail.signature")}
          </span>
          <AnimatedTitle
            title={title}
            className="text-5xl md:text-7xl lg:text-8xl text-white mb-8"
          />
          <div className="flex gap-6 text-white/80 text-sm uppercase tracking-wider flex-wrap mb-10">
            {features.map((feature: string, i: number) => (
              <span key={i} className="flex items-center gap-3">
                {t(`rooms.features.${feature}`, { defaultValue: feature })}
                {i < features.length - 1 && <span className="mx-2">•</span>}
              </span>
            ))}
          </div>
        </div>
      </section>



      {/* Gallery Grid */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="mb-8">
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("accommodation.detail.gallerySub")}</span>
            <h3 className="text-3xl font-luxury mt-2">{t("accommodation.detail.galleryTitle")}</h3>
          </div>
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 4 }
            }}
            navigation
            pagination={{ clickable: true }}
            className="pb-12"
          >
            {room.gallery && room.gallery.length > 0 && room.gallery[0] !== "" ? (
              room.gallery.map((img: string, i: number) => (
                <SwiperSlide key={i}>
                  <div
                    onClick={() => setLightboxIndex(i)}
                    className="relative overflow-hidden rounded-xl bg-black/40 border border-white/5 cursor-pointer aspect-[4/3] md:aspect-square group"
                  >
                    <img
                      src={img}
                      alt={`Gallery Image ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-750 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                      </svg>
                    </div>
                  </div>
                </SwiperSlide>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-gray-500">
                {t("accommodation.detail.noImages")}
              </div>
            )}
          </Swiper>
        </div>
      </section>

      
            <section className="pb-24 bg-background">
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2">
            <AnimatedText delay={0.1}>
              <h3 className="text-3xl font-luxury mb-6">{t("accommodation.detail.aboutRoom")}</h3>
              <p className="text-white/70 leading-relaxed mb-8">
                {description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 p-6 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <p className="text-gold text-xs uppercase tracking-widest mb-1">{t("accommodation.detail.roomSize")}</p>
                  <p className="text-white font-medium">{roomSize}</p>
                </div>
                <div>
                  <p className="text-gold text-xs uppercase tracking-widest mb-1">{t("accommodation.detail.bedSize")}</p>
                  <p className="text-white font-medium">{bedSize}</p>
                </div>
                <div>
                  <p className="text-gold text-xs uppercase tracking-widest mb-1">{t("accommodation.detail.view")}</p>
                  <p className="text-white font-medium">{view}</p>
                </div>
              </div>
            </AnimatedText>

            <AnimatedText delay={0.2}>
              <h3 className="text-2xl font-luxury mb-6">{t("accommodation.detail.amenities")}</h3>
              <ul className="grid grid-cols-2 gap-4 text-white/70">
                {amenities.map((amenity: string, i: number) => (
                  <li key={i} className="flex items-center gap-3">✓ {t(`rooms.amenities.${amenity.trim()}`, { defaultValue: amenity.trim() })}</li>
                ))}
              </ul>
            </AnimatedText>

            {inclusions.length > 0 && (
              <AnimatedText delay={0.25}>
                <h3 className="text-2xl font-luxury mb-6 mt-12">{t("accommodation.detail.inclusions")}</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-white/70">
                  {inclusions.map((inc: string, i: number) => (
                    <li key={i} className="flex items-center gap-3">✧ {t(`rooms.inclusions.${inc.trim()}`, { defaultValue: inc.trim() })}</li>
                  ))}
                </ul>
              </AnimatedText>
            )}

            {noted.length > 0 && (
              <AnimatedText delay={0.27}>
                <h3 className="text-2xl font-luxury mb-6 mt-12 text-gold">{t("accommodation.detail.pleaseNote")}</h3>
                <div className="p-6 bg-secondary/50 rounded-xl border border-white/5 space-y-3">
                  <ul className="list-disc pl-4 text-white/70 space-y-2">
                    {noted.map((note: string, i: number) => (
                      <li key={i}>{t(`rooms.noted.${note.trim()}`, { defaultValue: note.trim() })}</li>
                    ))}
                  </ul>
                </div>
              </AnimatedText>
            )}

            <AnimatedText delay={0.3}>
              <h3 className="text-2xl font-luxury mb-6 mt-12">{t("accommodation.detail.roomRules")}</h3>
              <div className="p-6 bg-secondary/50 rounded-xl border border-white/5">
                <p className="text-white/70 leading-relaxed whitespace-pre-line">
                  {roomRules}
                </p>
              </div>
            </AnimatedText>

            <AnimatedText delay={0.4}>
              <h3 className="text-2xl font-luxury mb-6 mt-12">{t("accommodation.detail.roomPolicy")}</h3>
              <div className="p-6 bg-secondary/50 rounded-xl border border-white/5 space-y-6 text-white/70">
                <div>
                  <h4 className="font-semibold text-white mb-2 uppercase tracking-wider text-xs text-gold">{t("accommodation.detail.childrenPolicies")}</h4>
                  <p className="leading-relaxed">
                    {t("accommodation.detail.childrenPolicyDesc")}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2 uppercase tracking-wider text-xs text-gold">{t("accommodation.detail.babyCot")}</h4>
                  <p className="leading-relaxed">
                    {t("accommodation.detail.babyCotDesc")}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2 uppercase tracking-wider text-xs text-gold">{t("accommodation.detail.extraBed")}</h4>
                  <p className="leading-relaxed">
                    {t("accommodation.detail.extraBedDesc")}
                  </p>
                </div>
                <div className="pt-4 border-t border-white/5 space-y-1">
                  <p className="flex items-center gap-2"><span className="text-red-500">✗</span> {t("accommodation.detail.noSmoking")}</p>
                  <p className="flex items-center gap-2"><span className="text-red-500">✗</span> {t("accommodation.detail.noPets")}</p>
                </div>
              </div>
            </AnimatedText>
          </div>

          <div className="lg:col-span-1">
            <AnimatedText delay={0.3} className="sticky top-32 bg-secondary p-8 rounded-2xl border border-white/5 shadow-2xl">
              <h4 className="text-2xl font-luxury mb-6">{t("accommodation.detail.reserve")} {title}</h4>
              
              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full block text-center px-6 py-4 bg-gold text-black hover:bg-white hover:text-black transition-colors uppercase tracking-widest text-sm font-semibold mb-4 cursor-pointer"
              >
                {t("accommodation.detail.directBooking", "Direct Booking From Us")}
              </button>
              
              <a
                href="https://www.booking.com/hotel/id/patal-kikian-sidemen.html"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full block text-center px-6 py-4 bg-[#003580] text-white hover:bg-[#00224f] transition-colors uppercase tracking-widest text-sm font-semibold mb-4 cursor-pointer border border-[#003580]"
              >
                {t("accommodation.detail.bookingCom", "Book on Booking.com")}
              </a>

              <p className="text-xs text-white/50 text-center leading-relaxed">
                {t("accommodation.detail.contactConcierge")}
              </p>
            </AnimatedText>
          </div>
        </div>
      </section>{/* Lightbox / Modal */}
      <AnimatePresence>
        {lightboxIndex !== null && room?.gallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 select-none"
            onClick={() => setLightboxIndex(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white cursor-pointer z-10 p-2"
              aria-label="Close Lightbox"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Left navigation arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : room.gallery.length - 1));
              }}
              className="absolute left-6 text-white/50 hover:text-white cursor-pointer bg-white/5 hover:bg-white/10 p-3 rounded-full transition-colors z-10"
              aria-label="Previous image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Main image container */}
            <div 
              className="relative max-w-5xl max-h-[80vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                key={room.gallery[lightboxIndex]}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                src={room.gallery[lightboxIndex]}
                alt={`Room Gallery ${lightboxIndex + 1}`}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
              
              <div className="text-center mt-4">
                <span className="text-gold text-xs uppercase tracking-widest font-semibold block mb-1">
                  {title}
                </span>
                <p className="text-gray-500 text-sm">
                  {lightboxIndex + 1} / {room.gallery.length}
                </p>
              </div>
            </div>

            {/* Right navigation arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev !== null && prev < room.gallery.length - 1 ? prev + 1 : 0));
              }}
              className="absolute right-6 text-white/50 hover:text-white cursor-pointer bg-white/5 hover:bg-white/10 p-3 rounded-full transition-colors z-10"
              aria-label="Next image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recommendation: Other Rooms you like */}
      <section className="py-24 bg-secondary/30 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="mb-12">
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">{t("accommodation.detail.otherChoices")}</span>
            <h3 className="text-3xl md:text-4xl font-luxury mt-2">{t("accommodation.detail.otherRooms")}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl">
            {otherRooms.map((otherRoom: any, i: number) => (
              <LuxuryCard
                key={otherRoom.slug}
                title={otherRoom.title}
                image={otherRoom.image}
                href={`/accommodation/${otherRoom.slug}`}
                description={otherRoom.description}
                features={otherRoom.features}
                delay={i * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal Popup */}
      {showBookingModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fade-in">
          <div className="relative w-full max-w-5xl bg-secondary border border-white/10 rounded-2xl p-5 sm:p-8 shadow-2xl animate-scale-up max-h-[95vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={() => setShowBookingModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors cursor-pointer p-2 z-55"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-4 mt-2">
              <span className="text-gold uppercase tracking-[0.2em] text-xs font-semibold block mb-1">{t("accommodation.detail.bookYourStay")}</span>
              <h3 className="text-2xl sm:text-3xl font-luxury text-white">{t("accommodation.detail.reserve")} {title}</h3>
            </div>

            {/* Embed Booking Widget */}
            <div className="py-2">
              <BookingWidget />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
